<template>
  <app-modal
    :modal-id="modalId"
    :title="dialogTitle"
    :preloader="preloader"
    @submit="submit"
    @close-modal="closeModal"
    modal-size="extra-large"
  >
    <template slot="header">
      <div class="employee-editor__modal-head">
        <div>
          <h5 class="modal-title mb-0" id="ModalLabel">{{ dialogTitle }}</h5>
          <small class="employee-editor__modal-subtitle">{{ dialogSubtitle }}</small>
        </div>
        <button type="button" class="close outline-none" @click.prevent="closeModal">
          <span>
            <app-icon :name="'x'" />
          </span>
        </button>
      </div>
    </template>

    <template slot="body">
      <app-overlay-loader v-if="preloader" />

      <div class="employee-editor" :class="{ 'loading-opacity': preloader }">
        <div v-if="successState.visible" class="employee-editor__success">
          <div class="employee-editor__success-hero">
            <div class="employee-editor__success-icon">
              <app-icon :name="'check-circle'" />
            </div>
            <div>
              <p class="employee-editor__eyebrow mb-2">{{ $t('success') }}</p>
              <h4 class="mb-2">{{ successTitle }}</h4>
              <p class="employee-editor__success-copy mb-0">{{ successMessage }}</p>
            </div>
          </div>

          <div class="employee-editor__success-grid">
            <section class="employee-editor__panel employee-editor__panel--success">
              <p class="employee-editor__eyebrow">{{ $t('employee') }}</p>
              <div class="employee-editor__success-profile">
                <div class="employee-editor__avatar-wrap employee-editor__avatar-wrap--success">
                  <img
                    v-if="employeePhotoPreview"
                    :src="employeePhotoPreview"
                    :alt="employeeDisplayName"
                    class="employee-editor__avatar-img"
                  />
                  <span v-else class="employee-editor__avatar-initials">{{ employeeInitials }}</span>
                </div>
                <div>
                  <h5 class="mb-1">{{ employeeDisplayName }}</h5>
                  <p class="employee-editor__success-copy mb-1">{{ inputs.email || $t('not_available') }}</p>
                  <p class="employee-editor__success-copy mb-0">{{ inputs.phone || $t('not_available') }}</p>
                </div>
              </div>
            </section>

            <section class="employee-editor__panel employee-editor__panel--success">
              <p class="employee-editor__eyebrow">{{ $t('face_enrollment') }}</p>
              <div class="employee-editor__success-list">
                <div class="employee-editor__success-row">
                  <span>{{ $t('status') }}</span>
                  <strong>{{ faceEnrollmentStatusLabel }}</strong>
                </div>
                <div class="employee-editor__success-row">
                  <span>{{ $t('face_quality_score') }}</span>
                  <strong>{{ faceQualityScoreLabel }}</strong>
                </div>
                <div class="employee-editor__success-row">
                  <span>{{ $t('Photo') }}</span>
                  <strong>{{ hasPhotoCandidate ? $t('ready') : $t('not_available') }}</strong>
                </div>
              </div>
            </section>

            <section class="employee-editor__panel employee-editor__panel--success">
              <p class="employee-editor__eyebrow">{{ $t('assignment_and_security') }}</p>
              <div class="employee-editor__success-list">
                <div class="employee-editor__success-row">
                  <span>{{ $t('location') }}</span>
                  <strong>{{ selectedLocationLabel }}</strong>
                </div>
                <div class="employee-editor__success-row">
                  <span>{{ $t('shift') }}</span>
                  <strong>{{ selectedShiftLabel }}</strong>
                </div>
                <div class="employee-editor__success-row">
                  <span>{{ $t('device_policy_enabled_label') }}</span>
                  <strong>{{ inputs.device_policy_enabled ? $t('enabled') : $t('disabled') }}</strong>
                </div>
              </div>
            </section>
          </div>
        </div>

        <template v-else>
          <div class="employee-editor__wizard-shell">
            <div class="employee-editor__steps">
              <button
                v-for="(step, index) in wizardSteps"
                :key="step.key"
                type="button"
                class="employee-editor__step"
                :class="{
                  'is-active': index === wizardStepIndex,
                  'is-complete': index < wizardStepIndex,
                  'is-available': index <= highestVisitedStepIndex
                }"
                @click.prevent="handleStepClick(index)"
              >
                <span class="employee-editor__step-index">
                  <app-icon v-if="index < wizardStepIndex" :name="'check'" />
                  <span v-else>{{ index + 1 }}</span>
                </span>
                <span class="employee-editor__step-copy">
                  <strong>{{ step.title }}</strong>
                  <small>{{ step.subtitle }}</small>
                </span>
              </button>
            </div>

            <form
              class="mb-0"
              ref="form"
              :data-url="selectedUrl ? `employees/${inputs.id}` : `employees`"
            >
              <div class="employee-editor__layout row no-gutters">
                <div class="col-xl-4 pr-xl-3 mb-3 mb-xl-0">
                  <section class="employee-editor__panel employee-editor__panel--profile">
                    <div class="employee-editor__avatar-wrap">
                      <img
                        v-if="employeePhotoPreview"
                        :src="employeePhotoPreview"
                        :alt="employeeDisplayName"
                        class="employee-editor__avatar-img"
                      />
                      <span v-else class="employee-editor__avatar-initials">{{ employeeInitials }}</span>
                    </div>

                    <div class="employee-editor__identity">
                      <h6 class="employee-editor__identity-name">{{ employeeDisplayName }}</h6>
                      <p class="employee-editor__identity-meta">{{ inputs.email || $t('email') }}</p>
                      <div class="employee-editor__chips">
                        <span class="employee-editor__chip" v-if="inputs.status">{{ inputs.status }}</span>
                        <span class="employee-editor__chip" v-if="selectedDepartmentLabel !== '-'">{{ selectedDepartmentLabel }}</span>
                        <span
                          class="employee-editor__chip"
                          :class="`employee-editor__chip--${faceEnrollmentTone}`"
                          v-if="faceEnrollmentStatusLabel"
                        >
                          {{ faceEnrollmentStatusLabel }}
                        </span>
                      </div>
                    </div>

                    <div class="employee-editor__summary-card">
                      <p class="employee-editor__eyebrow">{{ $t('summary') }}</p>
                      <div class="employee-editor__summary-row">
                        <span>{{ $t('role') }}</span>
                        <strong>{{ selectedJobTitleLabel }}</strong>
                      </div>
                      <div class="employee-editor__summary-row">
                        <span>{{ $t('location') }}</span>
                        <strong>{{ selectedLocationLabel }}</strong>
                      </div>
                      <div class="employee-editor__summary-row">
                        <span>{{ $t('shift') }}</span>
                        <strong>{{ selectedShiftLabel }}</strong>
                      </div>
                      <div class="employee-editor__summary-row">
                        <span>{{ $t('device_policy_enabled_label') }}</span>
                        <strong>{{ inputs.device_policy_enabled ? $t('enabled') : $t('disabled') }}</strong>
                      </div>
                    </div>

                    <div class="employee-editor__meta-card">
                      <div class="employee-editor__meta-card-head">
                        <h6>{{ $t('face_enrollment') }}</h6>
                        <span
                          class="employee-editor__status-badge"
                          :class="`employee-editor__status-badge--${faceEnrollmentTone}`"
                        >
                          {{ faceEnrollmentStatusLabel }}
                        </span>
                      </div>

                      <div class="employee-editor__meta-row">
                        <span>{{ $t('face_quality_score') }}</span>
                        <strong>{{ faceQualityScoreLabel }}</strong>
                      </div>
                      <div class="employee-editor__score-bar" aria-hidden="true">
                        <span :style="{ width: `${faceQualityScoreValue}%` }"></span>
                      </div>
                      <small class="employee-editor__meta-help">
                        {{ faceEnrollmentHelpText }}
                      </small>
                    </div>
                  </section>
                </div>

                <div class="col-xl-8">
                  <section class="employee-editor__panel employee-editor__panel--section">
                    <div class="employee-editor__section-head">
                      <div>
                        <p class="employee-editor__eyebrow mb-2">
                          {{ $t('step') }} {{ wizardStepIndex + 1 }} / {{ wizardSteps.length }}
                        </p>
                        <h6>{{ currentStepMeta.title }}</h6>
                        <p>{{ currentStepMeta.subtitle }}</p>
                      </div>
                    </div>

                    <div v-if="wizardStepIndex === 0" class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="phone" class="employee-editor__label">{{ $t('phone') }} <span class="required-dot">*</span></label>
                          <app-input
                            id="phone"
                            type="tel-input"
                            v-model="inputs.phone"
                            :placeholder="$t('phone')"
                            :required="true"
                          />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="email" class="employee-editor__label">{{ $t('email') }}</label>
                          <app-input
                            id="email"
                            type="text"
                            v-model="inputs.email"
                            :placeholder="$t('email')"
                          />
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="last_name" class="employee-editor__label">{{ $t('last_name') }} <span class="required-dot">*</span></label>
                          <app-input
                            id="last_name"
                            type="text"
                            v-model="inputs.last_name"
                            :placeholder="$t('last_name')"
                            :required="true"
                          />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="middle_name" class="employee-editor__label">{{ $t('middle_name') }}</label>
                          <app-input
                            id="middle_name"
                            type="text"
                            v-model="inputs.middle_name"
                            :placeholder="$t('middle_name')"
                          />
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="first_name" class="employee-editor__label">{{ $t('first_name') }} <span class="required-dot">*</span></label>
                          <app-input
                            id="first_name"
                            type="text"
                            v-model="inputs.first_name"
                            :placeholder="$t('first_name')"
                            :required="true"
                          />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="matricule" class="employee-editor__label">{{ $t('matricule') }} <span class="required-dot">*</span></label>
                          <app-input
                            id="matricule"
                            type="text"
                            v-model="inputs.matricule"
                            :placeholder="$t('matricule')"
                            :required="true"
                          />
                          <small class="text-muted d-block mt-1">
                            {{ $t('matricule_payroll_link_hint') }}
                          </small>
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="gender" class="employee-editor__label">{{ $t('gender') }} <span class="required-dot">*</span></label>
                          <app-input
                            class="w-100"
                            id="gender"
                            v-model="inputs.gender"
                            :list="genderList"
                            :placeholder="$t('choose_one')"
                            type="search-select"
                            :required="true"
                          />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="birth_date" class="employee-editor__label">{{ $t('birth_date') }}</label>
                          <app-input
                            id="birth_date"
                            type="date"
                            v-model="inputs.birth_date"
                            :placeholder="$t('birth_date')"
                          />
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="department_id" class="employee-editor__label">{{ $t('department') }}</label>
                          <app-input
                            class="w-100"
                            id="department_id"
                            v-model="inputs.department_id"
                            :list="departmentList"
                            list-value-field="name"
                            :placeholder="$t('choose_one')"
                            type="search-select"
                          />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="job_title_id" class="employee-editor__label">{{ $t('role') }}</label>
                          <app-input
                            class="w-100"
                            id="job_title_id"
                            v-model="inputs.job_title_id"
                            :list="jobTitleList"
                            list-value-field="name"
                            :placeholder="$t('choose_one')"
                            type="search-select"
                          />
                        </div>
                      </div>
                    </div>

                    <div v-else-if="wizardStepIndex === 1" class="employee-editor__face-step">
                      <div class="employee-editor__face-grid">
                        <div class="employee-editor__panel employee-editor__panel--subsection">
                          <div class="employee-editor__capture-head">{{ $t('employee_webcam_enroll_hint') }}</div>

                          <div v-if="cameraDevices.length" class="employee-editor__camera-selector">
                            <select
                              id="webcam_device_id"
                              class="form-control"
                              v-model="selectedCameraId"
                              @change="onCameraChange"
                            >
                              <option
                                v-for="camera in cameraDevices"
                                :key="camera.id"
                                :value="camera.id"
                              >
                                {{ camera.label }}
                              </option>
                            </select>
                          </div>

                          <div class="employee-editor__capture-actions">
                            <button
                              v-if="!webcamActive"
                              type="button"
                              class="btn btn-outline-primary btn-sm"
                              @click="startWebcam"
                            >
                              {{ $t('employee_webcam_start') }}
                            </button>
                            <template v-else>
                              <button
                                type="button"
                                class="btn btn-primary btn-sm"
                                @click="captureFromWebcam"
                              >
                                {{ $t('employee_webcam_capture') }}
                              </button>
                              <button
                                type="button"
                                class="btn btn-outline-secondary btn-sm"
                                @click="stopWebcam"
                              >
                                {{ $t('employee_webcam_stop') }}
                              </button>
                            </template>
                          </div>

                          <div v-if="webcamActive" class="employee-editor__capture-preview">
                            <video ref="webcamVideo" autoplay playsinline muted />
                          </div>
                          <canvas ref="webcamCanvas" class="employee-editor__capture-canvas" />

                          <small v-if="cameraError" class="text-danger d-block mt-2">
                            {{ cameraError }}
                          </small>
                        </div>

                        <div class="employee-editor__panel employee-editor__panel--subsection">
                          <label class="employee-editor__label">
                            {{ $t('Photo') }}
                            <span class="text-muted">({{ $t('optional') }})</span>
                          </label>
                          <app-input
                            type="custom-file-upload"
                            :label="$t('upload_your_file')"
                            v-model="photo"
                            @change="setImageChange"
                          />
                          <small class="text-muted font-italic d-block mt-2">
                            {{ $t('recommended_custom_file_validation') }} - {{ $t('photo_optional_note') }}
                          </small>

                          <div class="employee-editor__face-status-box">
                            <div>
                              <strong>{{ faceEnrollmentStatusLabel }}</strong>
                              <small>{{ faceEnrollmentHelpText }}</small>
                            </div>
                            <span
                              class="employee-editor__status-badge"
                              :class="`employee-editor__status-badge--${faceEnrollmentTone}`"
                            >
                              {{ faceQualityScoreLabel }}
                            </span>
                          </div>

                          <div class="employee-editor__sync-box">
                            <div class="employee-editor__sync-box-head">
                              <div>
                                <strong>{{ $t('employee_face_sync_status_title') }}</strong>
                                <small>{{ faceSyncSummaryText }}</small>
                              </div>

                              <button
                                v-if="isEditingExistingEmployee"
                                type="button"
                                class="btn btn-outline-primary btn-sm"
                                :disabled="!canRetryFaceSync"
                                @click="retryFaceSync"
                              >
                                {{ faceSyncRetryButtonLabel }}
                              </button>
                            </div>

                            <div class="employee-editor__sync-grid">
                              <div class="employee-editor__sync-item">
                                <span>{{ $t('status') }}</span>
                                <strong>{{ faceEnrollmentStatusLabel }}</strong>
                              </div>
                              <div
                                v-if="faceQualityScoreValue > 0"
                                class="employee-editor__sync-item"
                              >
                                <span>{{ $t('face_quality_score') }}</span>
                                <strong>{{ faceQualityScoreLabel }}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-else class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="assigned_location_id" class="employee-editor__label">{{ $t('location') }}</label>
                          <app-input
                            class="w-100"
                            id="assigned_location_id"
                            v-model="inputs.assigned_location_id"
                            :list="locationList"
                            list-value-field="name"
                            :placeholder="$t('choose_one')"
                            type="search-select"
                          />
                          <small class="text-muted d-block mt-1">
                            {{ $t('employee_default_location_hint') }}
                          </small>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="working_shift_id" class="employee-editor__label">{{ $t('shift') }} <span class="required-dot">*</span></label>
                          <app-input
                            class="w-100"
                            id="working_shift_id"
                            v-model="inputs.working_shift_id"
                            :list="workingShiftList"
                            list-value-field="name"
                            :placeholder="$t('choose_one')"
                            type="search-select"
                            :required="true"
                          />
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="status" class="employee-editor__label">{{ $t('status') }} <span class="required-dot">*</span></label>
                          <app-input
                            class="w-100"
                            id="status"
                            v-model="inputs.status"
                            :list="statusList"
                            :placeholder="$t('choose_one')"
                            type="search-select"
                            :required="true"
                          />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="shift_effective_from" class="employee-editor__label">{{ $t('created_at') }}</label>
                          <app-input
                            id="shift_effective_from"
                            type="date"
                            v-model="inputs.shift_effective_from"
                            :placeholder="$t('created_at')"
                          />
                        </div>
                      </div>

                      <div class="col-12">
                        <div class="form-group mb-0">
                          <label class="employee-editor__label d-block">{{ $t('device_policy_enabled_label') }}</label>
                          <button
                            type="button"
                            class="employee-editor__toggle"
                            :class="{ 'is-on': !!inputs.device_policy_enabled }"
                            :aria-pressed="`${!!inputs.device_policy_enabled}`"
                            @click="toggleDevicePolicy"
                          >
                            <span class="employee-editor__toggle-track">
                              <span class="employee-editor__toggle-thumb"></span>
                            </span>
                            <span class="employee-editor__toggle-copy">
                              <strong>{{ inputs.device_policy_enabled ? $t('enabled') : $t('disabled') }}</strong>
                              <small>{{ $t('device_policy_enabled_help') }}</small>
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </form>
          </div>
        </template>
      </div>
    </template>

    <template slot="footer">
      <div class="employee-editor__footer">
        <small v-if="!successState.visible" class="text-muted">* {{ $t('required') }}</small>
        <div class="employee-editor__actions">
          <button type="button" class="btn btn-secondary mr-2" data-dismiss="modal" @click="closeModal">
            {{ successState.visible ? $t('done') : $t('cancel') }}
          </button>

          <template v-if="!successState.visible">
            <button
              v-if="wizardStepIndex > 0"
              type="button"
              class="btn btn-outline-secondary mr-2"
              @click="previousWizardStep"
            >
              {{ $t('back') }}
            </button>
            <button
              v-if="!isFinalWizardStep"
              type="button"
              class="btn btn-primary"
              @click="nextWizardStep"
            >
              {{ $t('next') }}
            </button>
            <button
              v-else
              type="button"
              class="btn btn-primary"
              @click="submit"
            >
              {{ selectedUrl ? $t('employee_wizard_update_action') : $t('employee_wizard_save_action') }}
            </button>
          </template>
        </div>
      </div>
    </template>
  </app-modal>
</template>

<script>
import axios from 'axios';
import { FormMixin } from '../../../../core/mixins/form/FormMixin';
import { ModalMixin } from '../../../Mixins/ModalMixin';
import { mapState } from 'vuex';
import { formatDateForServer } from '../../../../common/Helper/Support/DateTimeHelper';

export default {
    name: 'EmployeeAddEditModal',
    mixins: [FormMixin, ModalMixin],
    props: {
        tableId: {
            type: String,
        },
    },
    computed: {
        ...mapState({
            locationList: state => state.selects.locationList,
            workingShiftList: state => state.selects.workingShiftList,
            departmentList: state => state.selects.departmentList,
            jobTitleList: state => state.selects.jobTitleList,
        }),
        wizardSteps() {
            return [
                {
                    key: 'info',
                    icon: 'user',
                    title: this.$t('employee_wizard_step_info'),
                    subtitle: this.$t('employee_wizard_step_info_subtitle'),
                },
                {
                    key: 'face',
                    icon: 'camera',
                    title: this.$t('employee_wizard_step_face'),
                    subtitle: this.$t('employee_wizard_step_face_subtitle'),
                },
                {
                    key: 'assignments',
                    icon: 'map-pin',
                    title: this.$t('employee_wizard_step_assignments'),
                    subtitle: this.$t('employee_wizard_step_assignments_subtitle'),
                },
            ];
        },
        currentStepMeta() {
            return this.wizardSteps[this.wizardStepIndex] || this.wizardSteps[0];
        },
        isFinalWizardStep() {
            return this.wizardStepIndex === this.wizardSteps.length - 1;
        },
        dialogTitle() {
            if (this.successState.visible) {
                return this.successTitle;
            }

            return this.selectedUrl
                ? this.$t('employee_wizard_edit_title')
                : this.$t('employee_wizard_create_title');
        },
        dialogSubtitle() {
            if (this.successState.visible) {
                return this.$t('employee_wizard_success_subtitle');
            }

            return this.currentStepMeta.subtitle || this.$t('please_fill_up_all_fields');
        },
        successTitle() {
            return this.selectedUrl
                ? this.$t('employee_wizard_success_update_title')
                : this.$t('employee_wizard_success_create_title');
        },
        successMessage() {
            return this.successState.message || this.$t('employee_wizard_success_subtitle');
        },
        employeeDisplayName() {
            const nameParts = [
                this.inputs.first_name,
                this.inputs.middle_name,
                this.inputs.last_name,
            ].filter(Boolean);

            return nameParts.length ? nameParts.join(' ') : this.$t('new-employee');
        },
        employeeInitials() {
            const initials = [this.inputs.first_name, this.inputs.last_name]
                .filter(Boolean)
                .map(name => name.trim().charAt(0).toUpperCase())
                .join('');

            return initials || 'EM';
        },
        employeePhotoPreview() {
            if (this.previewImage) {
                return this.previewImage;
            }

            if (typeof this.photo === 'string' && this.photo.trim().length) {
                return this.photo;
            }

            return null;
        },
        hasPhotoCandidate() {
            return !!this.employeePhotoPreview || this.photo instanceof File;
        },
        hasPersistedPhoto() {
            return typeof this.photo === 'string' && this.photo.trim().length > 0 && !this.imageChanged;
        },
        hasUnsavedPhotoCandidate() {
            return this.imageChanged && this.photo instanceof File;
        },
        isEditingExistingEmployee() {
            return !!this.selectedUrl && !!this.inputs.id;
        },
        faceEnrollmentStatusValue() {
            const status = `${this.inputs.face_enrollment_status || ''}`.trim().toLowerCase();

            if (['enrolled', 'failed', 'pending'].includes(status)) {
                return status;
            }

            return this.inputs.face_id ? 'enrolled' : 'pending';
        },
        faceEnrollmentStatusLabel() {
            return this.$t(this.faceEnrollmentStatusValue);
        },
        faceEnrollmentTone() {
            switch (this.faceEnrollmentStatusValue) {
                case 'enrolled':
                    return 'success';
                case 'failed':
                    return 'danger';
                default:
                    return 'warning';
            }
        },
        faceQualityScoreValue() {
            const rawValue = Number(this.inputs.face_quality_score);

            if (!Number.isFinite(rawValue) || rawValue < 0) {
                return 0;
            }

            return Math.max(0, Math.min(100, Math.round(rawValue)));
        },
        faceQualityScoreLabel() {
            return this.faceQualityScoreValue > 0
                ? `${this.faceQualityScoreValue}%`
                : this.$t('not_available');
        },
        faceEnrollmentHelpText() {
            if (this.faceEnrollmentStatusValue === 'enrolled') {
                return this.$t('face_enrollment_ready_help');
            }

            if (this.faceEnrollmentStatusValue === 'failed') {
                return this.$t('face_enrollment_failed_help');
            }

            if (this.hasPhotoCandidate) {
                return this.$t('employee_face_ready_to_submit_help');
            }

            return this.$t('face_enrollment_pending_help');
        },
        canRetryFaceSync() {
            return this.isEditingExistingEmployee
                && this.hasPersistedPhoto
                && !this.hasUnsavedPhotoCandidate
                && !this.faceSyncRetrying
                && !this.preloader;
        },
        faceSyncRetryButtonLabel() {
            return this.faceSyncRetrying
                ? this.$t('employee_face_sync_retrying')
                : this.$t('employee_face_sync_retry_action');
        },
        faceSyncSummaryText() {
            if (!this.isEditingExistingEmployee) {
                return this.$t('employee_face_sync_retry_available_after_save');
            }

            if (this.hasUnsavedPhotoCandidate) {
                return this.$t('employee_face_sync_retry_save_pending_photo');
            }

            if (this.faceEnrollmentStatusValue === 'enrolled') {
                return this.$t('employee_face_sync_ready_help');
            }

            if (this.hasPersistedPhoto) {
                return this.$t('employee_face_sync_retry_help');
            }

            return this.$t('employee_face_sync_retry_no_photo_help');
        },
        selectedLocationLabel() {
            return this.findOptionLabel(this.locationList, this.inputs.assigned_location_id, 'name');
        },
        selectedShiftLabel() {
            return this.findOptionLabel(this.workingShiftList, this.inputs.working_shift_id, 'name');
        },
        selectedDepartmentLabel() {
            return this.findOptionLabel(this.departmentList, this.inputs.department_id, 'name');
        },
        selectedJobTitleLabel() {
            return this.findOptionLabel(this.jobTitleList, this.inputs.job_title_id, 'name');
        },
    },
    data() {
        return {
            preloader: false,
            modalId: 'employee-add-edit-modal',
            inputs: {
                assigned_location_id: '',
                payroll_currency: 'USD',
                contract_type: 'permanent',
                status: 'active',
                device_policy_enabled: false,
                face_enrollment_status: 'pending',
                face_quality_score: null,
                shift_effective_from: new Date(),
                leave_days: 0,
                extra_fees: 0,
                dependents: 0,
                children: 0,
            },
            photo: {},
            previewImage: null,
            imageChanged: false,
            webcamActive: false,
            cameraStream: null,
            cameraError: '',
            cameraDevices: [],
            selectedCameraId: '',
            faceSyncRetrying: false,
            wizardStepIndex: 0,
            highestVisitedStepIndex: 0,
            successState: {
                visible: false,
                message: '',
            },
            statusList: [
                {
                    id: 'active',
                    value: this.$t('active')
                },
                {
                    id: 'suspended',
                    value: this.$t('suspended')
                },
                {
                    id: 'terminated',
                    value: this.$t('terminated')
                },
            ],
            genderList: [
                {
                    id: 'male',
                    value: this.$t('male')
                },
                {
                    id: 'female',
                    value: this.$t('female')
                }
            ],
        }
    },
    methods: {
        findOptionLabel(list, value, labelField = 'value') {
            if (!value) {
                return '-';
            }

            const matchedOption = (list || []).find(item => `${item.id}` === `${value}`);
            if (!matchedOption) {
                return '-';
            }

            return matchedOption[labelField] || matchedOption.value || matchedOption.name || '-';
        },
        handleStepClick(index) {
            if (index <= this.highestVisitedStepIndex) {
                this.setWizardStep(index);
                return;
            }

            if (index === this.wizardStepIndex + 1 && this.validateStep(this.wizardStepIndex)) {
                this.setWizardStep(index);
                this.highestVisitedStepIndex = index;
            }
        },
        setWizardStep(index) {
            if (this.wizardStepIndex === 1 && index !== 1) {
                this.stopWebcam();
            }

            this.wizardStepIndex = index;
        },
        nextWizardStep() {
            if (!this.validateStep(this.wizardStepIndex)) {
                return;
            }

            const nextIndex = Math.min(this.wizardStepIndex + 1, this.wizardSteps.length - 1);
            this.highestVisitedStepIndex = Math.max(this.highestVisitedStepIndex, nextIndex);
            this.setWizardStep(nextIndex);
        },
        previousWizardStep() {
            const previousIndex = Math.max(this.wizardStepIndex - 1, 0);
            this.setWizardStep(previousIndex);
        },
        getStepRequirements(stepIndex) {
            const requirements = {
                0: [
                    { key: 'phone', label: this.$t('phone') },
                    { key: 'last_name', label: this.$t('last_name') },
                    { key: 'first_name', label: this.$t('first_name') },
                    { key: 'matricule', label: this.$t('matricule') },
                    { key: 'gender', label: this.$t('gender') },
                ],
                1: [],
                2: [
                    { key: 'working_shift_id', label: this.$t('shift') },
                ],
            };

            return requirements[stepIndex] || [];
        },
        validateStep(stepIndex, shouldToast = true) {
            const missingField = this.getStepRequirements(stepIndex).find(field => {
                const value = this.inputs[field.key];
                return value === null || value === undefined || value === '';
            });

            if (!missingField) {
                return true;
            }

            if (shouldToast) {
                this.$toastr.e(`${missingField.label}: ${this.$t('this_field_is_required')}`);
            }

            return false;
        },
        validateAllSteps() {
            for (let stepIndex = 0; stepIndex < this.wizardSteps.length; stepIndex += 1) {
                if (!this.validateStep(stepIndex, false)) {
                    this.highestVisitedStepIndex = Math.max(this.highestVisitedStepIndex, stepIndex);
                    this.setWizardStep(stepIndex);
                    this.validateStep(stepIndex, true);
                    return false;
                }
            }

            return true;
        },
        submit() {
            if (this.successState.visible) {
                this.closeModal();
                return;
            }

            if (!this.validateAllSteps()) {
                return;
            }

            this.preloader = true;
            let url = this.selectedUrl ? `employees/${this.inputs.id}` : `employees`;
            let method = this.selectedUrl ? 'patch' : 'post';

            if (method === 'post') {
                let formData = new FormData();

                if (this.photo && this.photo instanceof File) {
                    formData.append('photo', this.photo);
                }

                const payload = {
                    phone: this.inputs.phone || '',
                    first_name: this.inputs.first_name || '',
                    last_name: this.inputs.last_name || '',
                    middle_name: this.inputs.middle_name || '',
                    email: this.inputs.email || '',
                    matricule: this.inputs.matricule || '',
                    gender: this.inputs.gender || '',
                    assigned_location_id: this.inputs.assigned_location_id || '',
                    birth_date: this.inputs.birth_date ? formatDateForServer(this.inputs.birth_date) : '',
                    contract_type: this.inputs.contract_type || 'permanent',
                    working_shift_id: this.inputs.working_shift_id || '',
                    job_title_id: this.inputs.job_title_id || '',
                    shift_effective_from: this.inputs.shift_effective_from
                      ? formatDateForServer(this.inputs.shift_effective_from)
                      : '',
                    status: this.inputs.status || 'active',
                    department_id: this.inputs.department_id || '',
                    device_policy_enabled: this.inputs.device_policy_enabled ? 1 : 0,
                    leave_days: this.inputs.leave_days ?? 0,
                    extra_fees: this.inputs.extra_fees ?? 0,
                    payroll_currency: (this.inputs.payroll_currency || 'USD').toUpperCase(),
                };

                Object.entries(payload).forEach(([key, value]) => {
                    formData.append(key, value);
                });

                axios({
                    method,
                    url,
                    data: formData,
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })
                .then(response => {
                    this.handleSubmitSuccess(response);
                })
                .catch(error => {
                    this.handleSubmitError(error);
                });
            } else {
                if (this.imageChanged && this.photo instanceof File) {
                    this.convertFileToBase64(this.photo).then(base64Photo => {
                        this.sendUpdateRequest(base64Photo);
                    }).catch(() => {
                        this.preloader = false;
                        this.$toastr.e(this.$t('employee_photo_processing_failed'));
                    });
                } else {
                    this.sendUpdateRequest(null);
                }
            }
        },
        handleSubmitSuccess(response) {
            this.preloader = false;
            this.stopWebcam();
            const employeePayload = this.extractEmployeePayload(response.data);
            const biometricWarning = this.resolveBiometricSyncWarning(response.data);

            if (employeePayload) {
                this.applyEmployeeSyncState(employeePayload);
            } else {
                this.inputs.face_enrollment_status = this.hasPhotoCandidate ? 'enrolled' : (this.inputs.face_enrollment_status || 'pending');
            }

            this.successState = {
                visible: true,
                message: this.$resolveMessage
                    ? this.$resolveMessage(response.data, {
                        key: this.selectedUrl ? 'employee_wizard_success_update_title' : 'employee_wizard_success_create_title'
                    })
                    : (response.data.message || ''),
            };

            if (biometricWarning) {
                this.$toastr.i(biometricWarning);
            }

            this.$hub.$emit('reload-' + this.tableId);
        },
        handleSubmitError(error) {
            this.preloader = false;
            const message = this.$resolveError(error, { key: 'failed_response' });
            this.$toastr.e(message);
        },
        resolveBiometricSyncWarning(responseData) {
            const biometricSync = responseData?.biometric_sync;

            if (!biometricSync || biometricSync.status !== 'failed') {
                return '';
            }

            if (typeof biometricSync.message === 'string' && biometricSync.message.trim()) {
                return biometricSync.message.trim();
            }

            if (this.$resolveMessage) {
                return this.$resolveMessage(biometricSync, { key: 'employee_face_sync_failed' });
            }

            return this.$t('employee_face_sync_failed');
        },
        extractEmployeePayload(responseData) {
            if (!responseData) {
                return null;
            }

            return responseData.employee || responseData?.data?.employee || null;
        },
        applyEmployeeSyncState(employeePayload) {
            if (!employeePayload) {
                return;
            }

            if (Object.prototype.hasOwnProperty.call(employeePayload, 'face_id')) {
                this.$set(this.inputs, 'face_id', employeePayload.face_id);
            }

            if (Object.prototype.hasOwnProperty.call(employeePayload, 'face_enrollment_status')) {
                this.$set(this.inputs, 'face_enrollment_status', employeePayload.face_enrollment_status || 'pending');
            }

            if (Object.prototype.hasOwnProperty.call(employeePayload, 'face_quality_score')) {
                this.$set(this.inputs, 'face_quality_score', employeePayload.face_quality_score);
            }

            if (
                Object.prototype.hasOwnProperty.call(employeePayload, 'photo')
                && typeof employeePayload.photo === 'string'
                && employeePayload.photo.trim().length
            ) {
                this.photo = employeePayload.photo;
                this.releasePreviewImage();
                this.previewImage = employeePayload.photo;
                this.imageChanged = false;
            }
        },
        async retryFaceSync() {
            if (!this.canRetryFaceSync) {
                return;
            }

            this.faceSyncRetrying = true;

            try {
                const response = await axios.post(`employees/${this.inputs.id}/resync-face`);
                this.applyEmployeeSyncState(this.extractEmployeePayload(response.data));

                const message = this.$resolveMessage
                    ? this.$resolveMessage(response.data, { key: 'employee_face_sync_successfully' })
                    : this.$t('employee_face_sync_successfully');

                this.$toastr.s(message);
            } catch (error) {
                const message = error?.response?.data
                    ? this.$resolveMessage(error.response.data, { key: 'employee_face_sync_failed' })
                    : this.$t('employee_face_sync_failed');

                this.$toastr.e(message);
            } finally {
                this.faceSyncRetrying = false;
            }
        },
        toggleDevicePolicy() {
            this.inputs.device_policy_enabled = !this.inputs.device_policy_enabled;
        },
        setImageChange() {
            this.stopWebcam();
            this.cameraError = '';
            this.imageChanged = true;
            this.releasePreviewImage();

            if (this.photo instanceof File) {
                this.previewImage = URL.createObjectURL(this.photo);
            }
        },
        async refreshCameraDevices() {
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                this.cameraDevices = [];
                this.selectedCameraId = '';
                return;
            }

            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const cameras = devices
                    .filter(device => device.kind === 'videoinput')
                    .map((device, index) => ({
                        id: device.deviceId,
                        label: device.label || `${this.$t('webcam')} ${index + 1}`,
                    }));

                this.cameraDevices = cameras;

                if (!this.cameraDevices.length) {
                    this.selectedCameraId = '';
                    return;
                }

                const hasSelectedCamera = this.cameraDevices.some(camera => camera.id === this.selectedCameraId);
                if (!hasSelectedCamera) {
                    this.selectedCameraId = this.cameraDevices[0].id;
                }
            } catch (error) {
                this.cameraDevices = [];
            }
        },
        async onCameraChange() {
            if (this.webcamActive) {
                await this.startWebcam();
            }
        },
        async handleDeviceChange() {
            await this.refreshCameraDevices();
        },
        async startWebcam() {
            this.cameraError = '';

            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.cameraError = this.$t('employee_webcam_not_supported');
                return;
            }

            try {
                this.stopWebcam();
                const preferredVideoConstraint = this.selectedCameraId
                    ? { deviceId: { exact: this.selectedCameraId } }
                    : { facingMode: 'user' };

                try {
                    this.cameraStream = await navigator.mediaDevices.getUserMedia({
                        video: preferredVideoConstraint,
                        audio: false
                    });
                } catch (error) {
                    this.cameraStream = await navigator.mediaDevices.getUserMedia({
                        video: true,
                        audio: false
                    });
                }
                this.webcamActive = true;

                await this.$nextTick();
                const webcamVideo = this.$refs.webcamVideo;

                if (webcamVideo) {
                    webcamVideo.srcObject = this.cameraStream;
                    const playPromise = webcamVideo.play();
                    if (playPromise && typeof playPromise.then === 'function') {
                        await playPromise.catch(() => {});
                    }
                }

                const activeTrack = this.cameraStream.getVideoTracks()[0];
                const activeTrackDeviceId = activeTrack && activeTrack.getSettings
                    ? activeTrack.getSettings().deviceId
                    : '';

                if (activeTrackDeviceId) {
                    this.selectedCameraId = activeTrackDeviceId;
                }

                await this.refreshCameraDevices();
            } catch (error) {
                this.stopWebcam();
                this.cameraError = this.$t('employee_webcam_access_error');
            }
        },
        captureFromWebcam() {
            const webcamVideo = this.$refs.webcamVideo;
            const webcamCanvas = this.$refs.webcamCanvas;

            if (!webcamVideo || !webcamCanvas || !webcamVideo.videoWidth || !webcamVideo.videoHeight) {
                this.cameraError = this.$t('employee_webcam_stream_not_ready');
                return;
            }

            const squareSize = Math.min(webcamVideo.videoWidth, webcamVideo.videoHeight);
            const sourceX = Math.max(0, Math.floor((webcamVideo.videoWidth - squareSize) / 2));
            const sourceY = Math.max(0, Math.floor((webcamVideo.videoHeight - squareSize) / 2));

            webcamCanvas.width = squareSize;
            webcamCanvas.height = squareSize;

            const context = webcamCanvas.getContext('2d');
            if (!context) {
                this.cameraError = this.$t('employee_webcam_capture_unavailable');
                return;
            }

            context.clearRect(0, 0, squareSize, squareSize);
            context.drawImage(
                webcamVideo,
                sourceX,
                sourceY,
                squareSize,
                squareSize,
                0,
                0,
                squareSize,
                squareSize
            );

            webcamCanvas.toBlob((blob) => {
                if (!blob) {
                    this.cameraError = this.$t('employee_webcam_capture_failed');
                    return;
                }

                this.photo = new File(
                    [blob],
                    `employee-webcam-${Date.now()}.jpg`,
                    { type: 'image/jpeg' }
                );
                this.setImageChange();
            }, 'image/jpeg', 0.92);
        },
        stopWebcam() {
            if (this.cameraStream) {
                this.cameraStream.getTracks().forEach(track => track.stop());
                this.cameraStream = null;
            }

            const webcamVideo = this.$refs.webcamVideo;
            if (webcamVideo) {
                webcamVideo.srcObject = null;
            }

            this.webcamActive = false;
        },
        releasePreviewImage() {
            if (this.previewImage && this.previewImage.startsWith('blob:')) {
                URL.revokeObjectURL(this.previewImage);
            }
            this.previewImage = null;
        },
        convertFileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },
        sendUpdateRequest(base64Photo) {
            let payload = {
                phone: this.inputs.phone,
                first_name: this.inputs.first_name,
                last_name: this.inputs.last_name,
                middle_name: this.inputs.middle_name,
                email: this.inputs.email,
                employee_id: this.inputs.employee_id,
                matricule: this.inputs.matricule,
                gender: this.inputs.gender,
                assigned_location_id: this.inputs.assigned_location_id || null,
                birth_date: this.inputs.birth_date ? formatDateForServer(this.inputs.birth_date) : '',
                contract_type: this.inputs.contract_type || 'permanent',
                leave_days: this.inputs.leave_days ?? 0,
                extra_fees: this.inputs.extra_fees ?? 0,
                device_policy_enabled: !!this.inputs.device_policy_enabled,
                working_shift_id: this.inputs.working_shift_id,
                job_title_id: this.inputs.job_title_id,
                shift_effective_from: this.inputs.shift_effective_from
                  ? formatDateForServer(this.inputs.shift_effective_from)
                  : '',
                status: this.inputs.status || 'active',
                department_id: this.inputs.department_id,
                payroll_currency: (this.inputs.payroll_currency || 'USD').toUpperCase(),
            };

            if (this.imageChanged && base64Photo) {
                payload.photo = base64Photo;
            }

            axios.patch(`employees/${this.inputs.id}`, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            .then(response => {
                this.handleSubmitSuccess(response);
            })
            .catch(error => {
                this.handleSubmitError(error);
            });
        },
        closeModal() {
            this.stopWebcam();
            this.$emit('close-modal');
        },
        afterSuccess(response) {
            this.$toastr.s(response.data.message);
            this.$hub.$emit('reload-' + this.tableId);
        },
        afterSuccessFromGetEditData(response) {
            this.inputs = {
                assigned_location_id: '',
                payroll_currency: 'USD',
                contract_type: 'permanent',
                status: 'active',
                device_policy_enabled: false,
                face_enrollment_status: 'pending',
                face_quality_score: null,
                shift_effective_from: new Date(),
                leave_days: 0,
                extra_fees: 0,
                dependents: 0,
                children: 0,
                ...response.data,
            };
            this.inputs.device_policy_enabled = !!this.inputs.device_policy_enabled;
            this.photo = response.data.photo;
            this.inputs.birth_date = response.data.birth_date ? new Date(response.data.birth_date) : '';
            this.inputs.shift_effective_from = response.data.shift_effective_from
              ? new Date(response.data.shift_effective_from)
              : new Date();
            this.releasePreviewImage();
            this.previewImage = typeof response.data.photo === 'string' ? response.data.photo : null;
            this.imageChanged = false;
            this.preloader = false;
            this.wizardStepIndex = 0;
            this.highestVisitedStepIndex = 0;
        },
    },
    created() {
        this.$store.dispatch('getLocations');
        this.$store.dispatch('getWorkingShifts');
        this.$store.dispatch('getDepartments');
        this.$store.dispatch('getJobTitles');
        if (this.selectedUrl) {
            this.preloader = true;
        }
    },
    mounted() {
        this.refreshCameraDevices();

        if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
            navigator.mediaDevices.addEventListener('devicechange', this.handleDeviceChange);
        }
    },
    beforeDestroy() {
        if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
            navigator.mediaDevices.removeEventListener('devicechange', this.handleDeviceChange);
        }
        this.stopWebcam();
        this.releasePreviewImage();
    },
}
</script>

<style scoped>
.employee-editor {
  max-height: none;
  overflow-y: visible;
  padding-right: 0;
}

.employee-editor__modal-head {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.employee-editor__modal-subtitle {
  display: inline-block;
  margin-top: 0.2rem;
  color: var(--sidebar-text-muted);
  font-size: 0.78rem;
  letter-spacing: 0.01em;
}

.employee-editor__wizard-shell {
  display: grid;
  gap: 1rem;
}

.employee-editor__steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.employee-editor__step {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 0.95rem 1rem;
  border: 1px solid var(--default-border-color);
  border-radius: 16px;
  background: var(--default-card-bg);
  text-align: left;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.employee-editor__step.is-available:hover,
.employee-editor__step.is-available:focus {
  transform: translateY(-1px);
  border-color: rgba(59, 130, 246, 0.28);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  outline: none;
}

.employee-editor__step.is-active {
  border-color: rgba(59, 130, 246, 0.38);
  box-shadow: 0 10px 28px rgba(59, 130, 246, 0.1);
}

.employee-editor__step.is-complete {
  border-color: rgba(34, 197, 94, 0.28);
}

.employee-editor__step-index {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(148, 163, 184, 0.14);
  color: var(--default-font-color);
  font-weight: 700;
  flex-shrink: 0;
}

.employee-editor__step.is-active .employee-editor__step-index {
  background: rgba(59, 130, 246, 0.14);
  color: #1d4ed8;
}

.employee-editor__step.is-complete .employee-editor__step-index {
  background: rgba(34, 197, 94, 0.14);
  color: #15803d;
}

.employee-editor__step-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.employee-editor__step-copy strong {
  font-size: 0.86rem;
  color: var(--default-font-color);
}

.employee-editor__step-copy small {
  font-size: 0.74rem;
  color: var(--sidebar-text-muted);
  line-height: 1.4;
}

.employee-editor__layout {
  align-items: flex-start;
}

.employee-editor__panel {
  position: relative;
  border: 1px solid var(--default-border-color);
  border-radius: 16px;
  background: var(--default-card-bg);
}

.employee-editor__panel--profile {
  padding: 1.4rem;
  background-image: linear-gradient(135deg, rgba(59, 130, 246, 0.08), transparent 62%);
}

.employee-editor__panel--section {
  padding: 1.1rem 1.15rem 1rem;
}

.employee-editor__panel--subsection {
  padding: 1rem;
}

.employee-editor__panel--success {
  padding: 1.1rem;
}

.employee-editor__section-head {
  margin-bottom: 0.9rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid var(--default-border-color);
}

.employee-editor__section-head h6 {
  margin-bottom: 0.15rem;
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--default-font-color);
  letter-spacing: 0.01em;
}

.employee-editor__section-head p {
  margin-bottom: 0;
  font-size: 0.78rem;
  color: var(--sidebar-text-muted);
}

.employee-editor__eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--sidebar-text-muted);
}

.employee-editor__avatar-wrap {
  width: 132px;
  height: 132px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(59, 130, 246, 0.22);
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(6, 182, 212, 0.14));
  margin-bottom: 0.9rem;
  overflow: hidden;
}

.employee-editor__avatar-wrap--success {
  width: 92px;
  height: 92px;
  margin-bottom: 0;
  border-radius: 18px;
}

.employee-editor__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.employee-editor__avatar-initials {
  font-size: 1.48rem;
  font-weight: 600;
  color: var(--mozali-color-brand);
}

.employee-editor__identity-name {
  margin-bottom: 0.2rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--default-font-color);
  word-break: break-word;
}

.employee-editor__identity-meta {
  margin-bottom: 0.6rem;
  font-size: 0.82rem;
  color: var(--sidebar-text-muted);
  word-break: break-word;
}

.employee-editor__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.employee-editor__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.18rem 0.58rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  color: #1e3a8a;
  background: rgba(59, 130, 246, 0.16);
}

.employee-editor__chip--success {
  color: #166534;
  background: rgba(34, 197, 94, 0.16);
}

.employee-editor__chip--warning {
  color: #92400e;
  background: rgba(245, 158, 11, 0.16);
}

.employee-editor__chip--danger {
  color: #991b1b;
  background: rgba(239, 68, 68, 0.16);
}

.employee-editor__summary-card {
  margin-top: 1rem;
  padding: 0.95rem 1rem;
  border-radius: 14px;
  border: 1px solid var(--mozali-border-soft);
  background: var(--mozali-surface-overlay);
}

.employee-editor__summary-row,
.employee-editor__success-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.79rem;
  color: var(--sidebar-text-muted);
}

.employee-editor__summary-row + .employee-editor__summary-row,
.employee-editor__success-row + .employee-editor__success-row {
  margin-top: 0.55rem;
}

.employee-editor__summary-row strong,
.employee-editor__success-row strong {
  color: var(--default-font-color);
  text-align: right;
}

.employee-editor__meta-card {
  margin-top: 1rem;
  padding: 0.95rem 1rem;
  border-radius: 14px;
  border: 1px solid var(--mozali-border-soft);
  background: var(--mozali-surface-overlay);
}

.employee-editor__meta-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  margin-bottom: 0.75rem;
}

.employee-editor__meta-card-head h6 {
  margin-bottom: 0;
  font-size: 0.86rem;
  font-weight: 600;
}

.employee-editor__status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.22rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}

.employee-editor__status-badge--success {
  color: #166534;
  background: rgba(34, 197, 94, 0.16);
}

.employee-editor__status-badge--warning {
  color: #92400e;
  background: rgba(245, 158, 11, 0.16);
}

.employee-editor__status-badge--danger {
  color: #991b1b;
  background: rgba(239, 68, 68, 0.16);
}

.employee-editor__meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.79rem;
  color: var(--sidebar-text-muted);
}

.employee-editor__meta-row strong {
  color: var(--default-font-color);
}

.employee-editor__score-bar {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.2);
}

.employee-editor__score-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #38bdf8, #2563eb);
}

.employee-editor__meta-help {
  display: block;
  margin-top: 0.55rem;
  font-size: 0.75rem;
  color: var(--sidebar-text-muted);
  line-height: 1.45;
}

.employee-editor__face-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.employee-editor__capture-head {
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--sidebar-text-muted);
}

.employee-editor__camera-selector {
  margin-top: 0.55rem;
}

.employee-editor__camera-selector .form-control {
  min-height: 36px;
  border-radius: 9px;
  font-size: 0.82rem;
}

.employee-editor__capture-actions {
  margin-top: 0.65rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.employee-editor__capture-preview {
  margin-top: 0.75rem;
  width: min(100%, 260px);
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  border: 1px solid var(--default-border-color);
  overflow: hidden;
  background: #0f172a;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
}

.employee-editor__capture-preview video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transform: scaleX(-1);
  transform-origin: center;
}

.employee-editor__capture-canvas {
  display: none;
}

.employee-editor__face-status-box {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.9rem;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.06);
  border: 1px dashed rgba(59, 130, 246, 0.18);
}

.employee-editor__face-status-box strong {
  display: block;
  color: var(--default-font-color);
}

.employee-editor__face-status-box small {
  display: block;
  margin-top: 0.18rem;
  font-size: 0.74rem;
  color: var(--sidebar-text-muted);
  line-height: 1.45;
}

.employee-editor__sync-box {
  margin-top: 0.9rem;
  padding: 0.95rem 1rem;
  border-radius: 14px;
  border: 1px solid var(--mozali-border-soft);
  background: var(--mozali-surface-muted);
  display: grid;
  gap: 0.85rem;
}

.employee-editor__sync-box-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.9rem;
}

.employee-editor__sync-box-head strong {
  display: block;
  color: var(--default-font-color);
}

.employee-editor__sync-box-head small {
  display: block;
  margin-top: 0.18rem;
  color: var(--sidebar-text-muted);
  line-height: 1.45;
}

.employee-editor__sync-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.employee-editor__sync-item {
  padding: 0.78rem 0.82rem;
  border-radius: 12px;
  background: var(--mozali-surface-overlay-strong);
  border: 1px solid var(--mozali-border-soft);
}

.employee-editor__sync-item span {
  display: block;
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--sidebar-text-muted);
}

.employee-editor__sync-item strong {
  display: block;
  margin-top: 0.28rem;
  color: var(--default-font-color);
  word-break: break-word;
}

.employee-editor__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.85rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid var(--default-border-color);
  border-radius: 12px;
  background: var(--default-card-bg);
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.employee-editor__toggle:hover,
.employee-editor__toggle:focus {
  border-color: rgba(59, 130, 246, 0.4);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
  outline: none;
}

.employee-editor__toggle.is-on {
  background: rgba(59, 130, 246, 0.06);
  border-color: rgba(59, 130, 246, 0.34);
}

.employee-editor__toggle-track {
  position: relative;
  width: 46px;
  height: 26px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.4);
  flex-shrink: 0;
  transition: background-color 0.18s ease;
}

.employee-editor__toggle.is-on .employee-editor__toggle-track {
  background: rgba(37, 99, 235, 0.45);
}

.employee-editor__toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--default-card-bg);
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.18);
  transition: transform 0.18s ease;
}

.employee-editor__toggle.is-on .employee-editor__toggle-thumb {
  transform: translateX(20px);
}

.employee-editor__toggle-copy {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
}

.employee-editor__toggle-copy strong {
  font-size: 0.82rem;
  color: var(--default-font-color);
}

.employee-editor__toggle-copy small {
  font-size: 0.74rem;
  color: var(--sidebar-text-muted);
}

.employee-editor__label {
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--default-font-color);
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.required-dot {
  color: #ef4444;
  font-weight: 700;
}

.employee-editor__success {
  display: grid;
  gap: 1rem;
}

.employee-editor__success-hero {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem 1.3rem;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(59, 130, 246, 0.1));
  border: 1px solid rgba(34, 197, 94, 0.18);
}

.employee-editor__success-icon {
  width: 60px;
  height: 60px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(34, 197, 94, 0.18);
  color: #15803d;
  flex-shrink: 0;
}

.employee-editor__success-icon ::v-deep svg {
  width: 28px;
  height: 28px;
}

.employee-editor__success-copy {
  color: var(--sidebar-text-muted);
  line-height: 1.5;
}

.employee-editor__success-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.employee-editor__success-profile {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.employee-editor__success-list {
  margin-top: 0.5rem;
}

.employee-editor__footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.employee-editor__actions {
  display: inline-flex;
  align-items: center;
}

.employee-editor ::v-deep .form-group {
  margin-bottom: 0.95rem;
}

.employee-editor ::v-deep input,
.employee-editor ::v-deep select,
.employee-editor ::v-deep textarea,
.employee-editor ::v-deep .dropdown-toggle,
.employee-editor ::v-deep .vs__dropdown-toggle {
  border-radius: 10px !important;
  border-color: var(--default-border-color) !important;
  min-height: 42px;
}

.employee-editor ::v-deep input:focus,
.employee-editor ::v-deep select:focus,
.employee-editor ::v-deep textarea:focus,
.employee-editor ::v-deep .dropdown-toggle:focus,
.employee-editor ::v-deep .vs__dropdown-toggle:focus {
  border-color: rgba(59, 130, 246, 0.72) !important;
}

@media (max-width: 1199.98px) {
  .employee-editor__face-grid,
  .employee-editor__success-grid,
  .employee-editor__steps {
    grid-template-columns: 1fr;
  }

  .employee-editor__panel--profile {
    padding: 1rem;
  }
}

@media (max-width: 767.98px) {
  .employee-editor__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .employee-editor__actions {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .employee-editor__sync-box-head {
    flex-direction: column;
    align-items: stretch;
  }

  .employee-editor__sync-grid {
    grid-template-columns: 1fr;
  }

  .employee-editor__success-hero,
  .employee-editor__success-profile {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
